/* ¿La zona de este chofer es provincia de Buenos Aires?
 *
 * ── PARA QUÉ SE PREGUNTA ─────────────────────────────────────────────────
 *
 * Un chofer de **provincia de Buenos Aires** tiene **60 días de prórroga**
 * después de que se le vence el registro: puede salir igual. Eso lo decide
 * esta función, así que equivocarse acá no es un detalle de pantalla — deja
 * salir a alguien que no puede, o frena a alguien que sí.
 *
 * ── CABA NO ENTRA, Y ES UNA DECISIÓN, NO UN OLVIDO ───────────────────────
 *
 * La Ciudad es una jurisdicción propia y emite su propio registro; la
 * prórroga es de la Provincia. Son 8 choferes y están explícitamente en la
 * lista de «otra», no en el limbo de las desconocidas: así el día que alguien
 * se pregunte por qué no la tienen, la respuesta está escrita.
 *
 * ── CUATRO RESPUESTAS Y NO DOS ───────────────────────────────────────────
 *
 *   buenos_aires   le corresponde la prórroga
 *   otra           otra provincia — no le corresponde
 *   sin_dato       la celda está vacía (hoy son 10)
 *   desconocida    HAY TEXTO Y NO LO RECONOCEMOS
 *
 * **La cuarta es la que hace que esto no se pudra.** Sin ella, una zona nueva
 * o mal tipeada caería en «otra» y ese chofer perdería su prórroga **sin que
 * nadie se entere** — la pantalla diría «no puede salir» y sería mentira, y el
 * síntoma no señalaría a la lista de zonas. Es la misma idea que
 * `permisos_desconocidos()` en la planilla de FlotaBot: lo que no se reconoce
 * se MUESTRA, no se adivina ni se arregla solo.
 *
 * Ojo con la dirección del error: `desconocida` **no** da prórroga. Ante la
 * duda no se deja salir a nadie — pero se dice por qué, que es lo que permite
 * arreglarlo en un minuto agregando la zona a la lista.
 *
 * ── LOS ACENTOS ESTABAN ROTOS ────────────────────────────────────────────
 *
 * La planilla se bajaba con el charset equivocado y en la base había 12 zonas
 * en doble codificación (`MORÃN` por MORÓN). Ya se arregló en los dos lados
 * —el origen en `documents.py` y las filas con una migración— pero acá igual
 * se comparan **sin acentos**: nadie escribe «MORÓN» igual dos veces, y una
 * comparación que depende de una tilde es una comparación que un día falla.
 */

/* Mayúsculas, sin acentos, los puntos como espacio y los espacios colapsados.
 * El punto va a ESPACIO y no a nada: así «BS.AS.» y «BS. AS.» —las dos formas
 * que están cargadas hoy— caen en la misma clave. Borrándolo darían «BSAS» y
 * «BS AS», que es justo el duplicado que esto viene a evitar. */
export function normalizar(zona) {
  return String(zona ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")   // los diacríticos que NFD dejó sueltos
    .toUpperCase()
    .replace(/[.,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/* Los partidos bonaerenses. Salen de lo que hay CARGADO —se listaron los 69
 * valores distintos de la planilla antes de escribir esto— más un puñado de
 * partidos grandes que no están hoy pero van a aparecer.
 *
 * NO SE INVENTA UNA LISTA LARGA "por las dudas": cada entrada de más es una
 * prórroga que se podría dar mal, y `desconocida` ya avisa de las que faltan.
 * Agregar una zona nueva es una línea acá, con la pantalla diciendo cuál. */
export const BUENOS_AIRES = new Set([
  // Cargados hoy en la planilla.
  "TIGRE", "PILAR", "HURLINGHAM", "MERLO", "LA MATANZA", "EZEIZA", "ESCOBAR",
  "MALV ARG", "FL VARELA", "JOSE C PAZ", "GRAL RODRIGUEZ", "QUILMES",
  "SAN MIGUEL", "ALTE BROWN", "AVELLANEDA", "MORENO", "LOMAS DE ZAMORA",
  "L DE ZAMORA", "BS AS", "BSAS", "BUENOS AIRES", "GRAL S MARTIN",
  "GRAL SAN MARTIN", "SAN MARTIN", "MORON", "SAN ISIDRO", "BOLIVAR",
  "3 DE FEBRERO", "MARCOS PAZ", "25 DE MAYO", "LUJAN", "PDO DE LA COSTA",
  "EST ECHEVERRIA", "LAS FLORES", "SAN VICENTE", "ENSENADA",
  "EXLT DE LA CRUZ", "NAVARRO", "BERAZATEGUI", "BAHIA BLANCA", "CANUELAS",
  "VILLA MARTELLI", "LANUS", "ZARATE", "CNEL ROSALES", "ITUZAINGO",
  "SAN FERNANDO", "OLAVARRIA",
  // Todavía no aparecen, y son inconfundibles.
  "LA PLATA", "VICENTE LOPEZ", "MAR DEL PLATA", "CAMPANA", "TANDIL",
  "NECOCHEA", "PERGAMINO", "CHIVILCOY", "BRANDSEN", "CHASCOMUS",
  "SAN NICOLAS", "GRAL PUEYRREDON", "TRES DE FEBRERO", "ESTEBAN ECHEVERRIA",
  "FLORENCIO VARELA", "ALMIRANTE BROWN", "MALVINAS ARGENTINAS",
  "PARTIDO DE LA COSTA", "GENERAL RODRIGUEZ", "GENERAL SAN MARTIN",
]);

/* Las que NO son provincia, escritas a propósito en vez de dejarlas caer en
 * «desconocida». Una zona que se sabe que no corresponde no es un pendiente:
 * mezclarla con las que hay que revisar llenaría esa lista de ruido y ahí se
 * deja de mirar — la lección del reporte semanal de vencimientos. */
export const OTRAS = new Set([
  // CABA es la que más se va a preguntar. Ver el encabezado.
  "CABA", "CAPITAL FEDERAL", "CIUDAD DE BUENOS AIRES",
  "RGD", "RIO GRANDE", "USHUAHIA", "USHUAIA", "TIERRA DEL FUEGO",
  "TUCUMAN", "P DE LOS LIBRES", "PASO DE LOS LIBRES", "MISIONES",
  "SANTA FE", "CORRIENTES", "SIERRA GRANDE", "PTO MADRYN", "PUERTO MADRYN",
  "RIO NEGRO", "RIO GALLEGOS", "R GALLEGOS", "CORDOBA", "ENTRE RIOS",
  "CHUBUT", "STGO DEL ESTERO", "SANTIAGO DEL ESTERO", "SANTA CRUZ",
  "LA PAMPA", "RIO COLORADO", "SALTA", "NEUQUEN", "MENDOZA", "SAN JUAN",
  "SAN LUIS", "CHACO", "FORMOSA", "JUJUY", "CATAMARCA", "LA RIOJA",
]);

export const BUENOS_AIRES_ZONA = "buenos_aires";
export const OTRA = "otra";
export const SIN_DATO = "sin_dato";
export const DESCONOCIDA = "desconocida";

export function clasificar(zona) {
  const z = normalizar(zona);
  if (!z) return SIN_DATO;
  if (BUENOS_AIRES.has(z)) return BUENOS_AIRES_ZONA;
  if (OTRAS.has(z)) return OTRA;
  return DESCONOCIDA;
}

/* La pregunta que hace `documentacion.evaluar`. Es `true` SÓLO para la
 * provincia: `sin_dato` y `desconocida` no dan prórroga, porque ante la duda
 * nadie sale de más. */
export const esBuenosAires = (zona) => clasificar(zona) === BUENOS_AIRES_ZONA;

/* Las que hay que revisar, agrupadas y con cuántos choferes tiene cada una.
 * Se muestran juntas y no una por fila: cuatro carteles diciendo lo mismo se
 * leen como cuatro problemas distintos. Misma decisión que las llaves muertas
 * de la pantalla de permisos. */
export function desconocidas(filas) {
  const cuenta = new Map();
  for (const f of filas || []) {
    if (clasificar(f?.zona) !== DESCONOCIDA) continue;
    const z = String(f.zona).trim();
    cuenta.set(z, (cuenta.get(z) || 0) + 1);
  }
  return [...cuenta.entries()]
    .map(([zona, choferes]) => ({ zona, choferes }))
    .sort((a, b) => b.choferes - a.choferes || a.zona.localeCompare(b.zona));
}
